import React from "react";

// Coding submissions tab — surfaces auto-graded test-case scores from
// amplehire_coding_submission_t plus integrity-flagging fields (paste_count,
// focus_blur_count). Rendered as cards, one per question.
//
// Server side: codingSubmissions from /api/candidate-report/{userId}/full.
// Each row already has integrityFlag computed; we still show the underlying
// counts so the recruiter can judge severity.
export default function CodingTab({ submissions }) {
  if (!Array.isArray(submissions) || submissions.length === 0) {
    return (
      <div className="text-center text-sm text-vf-muted py-10">
        No coding submissions on file for this candidate.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {submissions.map((s) => (
        <SubmissionCard key={s.submissionId} s={s} />
      ))}
    </div>
  );
}

function SubmissionCard({ s }) {
  const score = s.score == null ? null : Number(s.score);
  const passRatio = (s.totalTestCases && s.totalTestCases > 0)
    ? `${s.passedTestCases || 0}/${s.totalTestCases}`
    : null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-vf-muted">Question #{s.questionId} · {s.language}</div>
          <div className="text-sm font-medium text-vf-base mt-0.5 line-clamp-2" title={s.questionText}>
            {s.questionText || "(question text not loaded)"}
          </div>
          <div className="text-[11px] text-vf-muted mt-1">
            Submitted {s.submittedAt ? new Date(s.submittedAt).toLocaleString() : "—"}
            {s.status && ` · ${s.status}`}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-2xl font-bold tabular-nums ${scoreColor(score)}`}>
            {score == null ? "—" : score.toFixed(0) + "%"}
          </div>
          {passRatio && <div className="text-[11px] text-vf-muted">{passRatio} passed</div>}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <Stat label="Runtime" value={s.totalRuntimeMs == null ? "—" : `${s.totalRuntimeMs}ms`} />
        <Stat label="Peak memory" value={s.totalMemoryKb == null ? "—" : `${Math.round(s.totalMemoryKb / 1024)}MB`} />
        <Stat label="Time to first keystroke"
              value={s.timeToFirstKeystrokeMs == null ? "—" : msReadable(s.timeToFirstKeystrokeMs)} />
        <Stat label="Time to submit"
              value={s.timeToSubmitMs == null ? "—" : msReadable(s.timeToSubmitMs)} />
      </div>

      {(s.pasteCount > 0 || s.focusBlurCount > 0 || s.integrityFlag) && (
        <div className={`mt-3 p-2 rounded text-[11px] ${s.integrityFlag ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-slate-50 border border-slate-200 text-vf-muted"}`}>
          <b>{s.integrityFlag ? "🚩 Integrity flag" : "Integrity signals"}</b>:{" "}
          {s.pasteCount > 0 && <span className="mr-3">📋 {s.pasteCount} paste{s.pasteCount === 1 ? "" : "s"}</span>}
          {s.focusBlurCount > 0 && <span>↗ {s.focusBlurCount} tab/window blur{s.focusBlurCount === 1 ? "" : "s"}</span>}
          {s.pasteCount === 0 && s.focusBlurCount === 0 && <span>none observed</span>}
        </div>
      )}

      {s.errorMessage && (
        <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-[11px] text-red-700 font-mono whitespace-pre-wrap">
          {s.errorMessage}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
      <div className="text-[10px] text-vf-muted uppercase tracking-wide">{label}</div>
      <div className="text-vf-base font-medium tabular-nums">{value}</div>
    </div>
  );
}

const scoreColor = (s) => {
  if (s == null) return "text-slate-400";
  if (s >= 80) return "text-emerald-600";
  if (s >= 60) return "text-amber-600";
  if (s >= 40) return "text-orange-600";
  return "text-red-600";
};

function msReadable(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}
